import AlphaHelpers from '../../abstracts/classes/alpha-helpers'
import AlphaModule from '../../abstracts/classes/alpha-module'
import cloneDeep from 'lodash-es/cloneDeep'
import debounce from 'lodash-es/debounce'
import merge from 'lodash-es/merge'

export default class GsapWrapper extends AlphaModule {
  constructor(...args) {
    super(...args)

    this._gsap = null
    this._gsapPlugins = new Map()
    this._gsapState = AlphaHelpers.getLibStates().stateNone
    this._gsapResponsive = null
    this._gsapResponsiveState = AlphaHelpers.getLibStates().stateNone
  }

  getName() {
    return 'gsapWrapper'
  }

  getDefaultConfig() {
    const gsapParentConfig = this._getManager().getConfig().gsap
    const gsapPlugins = gsapParentConfig.plugins
    const gsapPresets = gsapParentConfig.presets
    const gsapAttribute = this._getAttributeName()
    const gsapResponsiveAttribute = this._getAttributeName('responsive')

    return {
      gsapAttribute,
      gsapSelector: `[${gsapAttribute}]`,
      gsapNotResponsiveSelector: `[${gsapAttribute}]:not([${gsapResponsiveAttribute}])`,
      gsapResponsiveAttribute,
      gsapResponsiveSelector: `[${gsapResponsiveAttribute}]`,
      gsapDataAttrs: ['from', 'to', 'preset'],
      gsapPlugins,
      gsapPresets
    }
  }

  _onLoad() {
    super._onLoad()

    this._getManager().addModuleEventListener(this.getName(), 'gsap.loaded', () => {
      // some elements require refresh when they're added to page / loaded (like images)
      this._handleDomChanges()
    }, true)

    // potentially load GSAP
    this._maybeLoadGsap()
  }

  _hasAnyGsapElement() {
    return document.querySelector(this.getConfig().gsapSelector) !== null
  }

  _hasAnyGsapResponsiveElement() {
    return document.querySelector(this.getConfig().gsapResponsiveSelector) !== null
  }

  async _maybeLoadGsap() {
    if (this._shouldLoadGsap()) {
      await this._loadGsapAsync()

      if (this._shouldLoadGsapPlugins()) {
        await this._loadGsapPlugins()
      }
    }

    if (this._shouldLoadGsapResponsive()) {
      await this._loadGsapResponsiveAsync()
    }

    const gsapElements = AlphaHelpers.smartQuerySelectorAll(this.getConfig().gsapNotResponsiveSelector)
    gsapElements.forEach((gsapEl) => this._initGsapElement(gsapEl))
  }

  _initGsapElement(gsapEl) {
    const gsapConfig = this._getGsapElementConfig(gsapEl)
    const from = gsapConfig.from || false
    const to = gsapConfig.to || false

    if (from || to) {
      const gsap = this.getGsap()
      const targets = gsapConfig._targets

      // reset any previous animation (needed for responsive)
      this._resetGsapTargetsAnimation(targets)

      // set new animation
      if (from && to) {
        gsap.fromTo(targets, from, to)
      } else if (from) {
        gsap.from(targets, from)
      } else {
        gsap.to(targets, to)
      }
    }
  }

  _getGsapElementConfig(gsapEl) {
    const presetsConfig = this.getConfig().gsapPresets
    let config = {}

    // use specified preset as base config
    const presetAttrName = this._getAttributeName('preset')
    if (gsapEl.hasAttribute(presetAttrName)) {
      const preset = gsapEl.getAttribute(presetAttrName)
      config = cloneDeep(presetsConfig[preset])
    }

    // use specified targets or fallback to current element
    const targetsAttrName = this._getAttributeName('targets')
    if (config.targets || gsapEl.hasAttribute(targetsAttrName)) {
      const targetsSelector = config.targets || gsapEl.getAttribute(targetsAttrName)
      config._targets = AlphaHelpers.smartQuerySelectorAll(targetsSelector, gsapEl)
    } else {
      config._targets = [gsapEl]
    }

    const gspAttrs = ['from', 'to']
    gspAttrs.forEach((gsapAttr) => {
      const attrName = this._getAttributeName(gsapAttr)
      if (gsapEl.hasAttribute(attrName)) {
        const attrValue = AlphaHelpers.getParsedJsonAttribute(gsapEl, attrName)
        config[gsapAttr] = merge(config[gsapAttr] || {}, attrValue)
      }

      // replace '_self' placeholder by current element in scrollTrigger
      if (gsapAttr in config && 'scrollTrigger' in config[gsapAttr]) {
        if (config[gsapAttr].scrollTrigger.trigger === '_self') {
          config[gsapAttr].scrollTrigger.trigger = gsapEl
        }
      }
    })

    return config
  }

  _resetGsapTargetsAnimation(targets) {
    // preserve some inline style properties set by other modules (ex: masonry)
    const targetEl = targets[0]
    const preservedProperties = ['position', 'left', 'top']
    const propertiesToClear = []
    const properties = [...targetEl.style]
    properties.forEach((property) => {
      if (!preservedProperties.includes(property) && property.indexOf('--') !== 0) {
        propertiesToClear.push(property)
      }
    })

    // kill all other animations
    const gsap = this.getGsap()
    gsap.killTweensOf(targets)
    gsap.set(targets, {
      clearProps: propertiesToClear.join(',')
    })
  }

  _getAttributeName(...parts) {
    parts = AlphaHelpers.noEmptyArrayValues(['gsap', ...parts])

    return `data-${parts.join('-')}`
  }

  _shouldLoadGsap() {
    return AlphaHelpers.getLibStates().stateNone === this.getGsapState() && this._hasAnyGsapElement()
  }

  _shouldLoadGsapPlugins() {
    return this.getConfig().gsapPlugins.length > 0
  }

  _shouldLoadGsapResponsive() {
    return AlphaHelpers.getLibStates().stateNone === this.getGsapResponsiveState() && this._hasAnyGsapResponsiveElement()
  }

  async _loadGsapAsync() {
    // mark GSAP as loading to ensure we don't load it multiple times
    this._gsapState = AlphaHelpers.getLibStates().stateLoading

    await import(/* webpackChunkName: "gsap" */ 'gsap')
      .then(({
        default: gsap
      }) => {
        this._gsap = gsap
      })

    // mark GSAP as loaded and inform other components
    this._gsapState = AlphaHelpers.getLibStates().stateLoaded

    this.dispatchEvent('gsap.loaded', {
      gsap: this.getGsap()
    })
  }

  async _loadGsapPlugins() {
    const gsap = this.getGsap()
    const config = this.getConfig()
    for (const pluginName of config.gsapPlugins) {
      // eslint-disable-next-line no-await-in-loop
      await import(
        /* webpackInclude: /(Draggable|EaselPlugin|Flip|MotionPathPlugin|Observer|PixiPlugin|ScrollToPlugin|ScrollTrigger|TextPlugin)\.js/ */
        /* webpackChunkName: "gsap-plugin-[request]" */
        `gsap/dist/${pluginName}`)
        .then(({
          default: plugin
        }) => {
          this._gsapPlugins.set(pluginName, plugin)

          gsap.registerPlugin(plugin)
        })
    }

    this.dispatchEvent('gsap.plugins.loaded', {
      gsap: this.getGsap()
    })
  }

  async _loadGsapResponsiveAsync() {
    // mark GSAP responsive as loading to ensure we don't load it multiple times
    this._gsapResponsiveState = AlphaHelpers.getLibStates().stateLoading

    await import(/* webpackChunkName: "gsap-responsive" */ '../../components/gsap-responsive')
      .then(({
        default: GsapResponsive
      }) => {
        this._gsapResponsive = new GsapResponsive(this)
      })

    // mark GSAP responsive as loaded and inform other components
    this._gsapResponsiveState = AlphaHelpers.getLibStates().stateLoaded

    this.dispatchEvent('gsap.responsive.loaded', {
      gsapResponsive: this.getGsapResponsive()
    })
  }

  _handleDomChanges() {
    if ('ResizeObserver' in window) {
      const mainEl = document.querySelector('.site-main')
      if (mainEl) {
        const debounceDelay = 100
        const onResize = debounce(() => {
          const scrollTrigger = this.getGsapPlugin('ScrollTrigger')
          if (scrollTrigger) {
            scrollTrigger.refresh()
          }
        }, debounceDelay)

        const resizeObserver = new ResizeObserver(onResize)
        resizeObserver.observe(mainEl)
      }
    }
  }

  getGsap() {
    return this._gsap
  }

  getGsapPlugin(pluginName) {
    return this._gsapPlugins.get(pluginName)
  }

  getGsapState(toString = false) {
    const gsapState = this._gsapState

    return toString ? AlphaHelpers.getLibStateString(gsapState) : gsapState
  }

  getGsapResponsive() {
    return this._gsapResponsive
  }

  getGsapResponsiveState(toString = false) {
    const gsapResponsiveState = this._gsapResponsiveState

    return toString ? AlphaHelpers.getLibStateString(gsapResponsiveState) : gsapResponsiveState
  }
}
